<template>
  <div class="calendar-bar">
    <div class="calendar-bar__label">
      <i class="far fa-calendar-alt mr-50"></i>
      <span>{{ $t('Date range') }}</span>
    </div>
    <div class="calendar-bar__pills">
      <button
          v-for="option in options"
          :key="option"
          type="button"
          class="calendar-bar__pill"
          :class="{ 'calendar-bar__pill--active': selected === option }"
          @click="onOptionSelected(option)"
      >
        <span class="calendar-bar__pill-label">{{ $t(`filters.picker.${option}`) }}</span>
      </button>

      <button
          id="calendar-bar-custom-range"
          type="button"
          class="calendar-bar__pill calendar-bar__pill--custom"
          :class="{ 'calendar-bar__pill--active': selected === 'custom' }"
      >
        <i class="far fa-calendar-alt mr-50 calendar-bar__pill-icon"></i>
        <span class="calendar-bar__pill-label">
          <template v-if="selected === 'custom' && customRange.start && customRange.end">
            {{ customRange.start | moment($t('timeFormat.date.format')) }}
            <span class="calendar-bar__range-arrow">→</span>
            {{ customRange.end | moment($t('timeFormat.date.format')) }}
          </template>
          <template v-else>{{ $t('filters.picker.custom') }}</template>
        </span>
      </button>
    </div>

    <b-popover
        target="calendar-bar-custom-range"
        :title="$t('Choose dates')"
        placement="bottomleft"
        triggers="click focus"
        custom-class="calendar-bar__popover"
    >
      <calendar-picker-range @input="val=>onOptionSelected('custom',val)"/>
    </b-popover>
  </div>
</template>


<script>
import CalendarPickerRange from '@/components/widget/picker/calendarPickerRange';
import mixinPopover from '@/components/mixin/mixinPopover';

export default {
  name: 'CalendarBar',
  mixins: [mixinPopover],
  components: { CalendarPickerRange },
  data: () => ({
    options: [
      'all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'last7Days', 'thisMonth', 'lastMonth', 'last30Days', 'thisYear'
    ],
    selected: 'thisWeek',
    customRange: {
      start: null,
      end: null
    },
  }),
  props: {
    value: {
      default: null
    },
  },
  created() {
    this.onOptionSelected(this.selected);
  },
  methods: {
    onOptionSelected(option, val) {
      this.selected = option;
      if (option === 'custom' && val) {
        this.customRange.start = val.start;
        this.customRange.end = val.end;
      }
      this.closeAllPopovers();
      this.$emit('input', this.getValue(option));
    },
    getValue(option) {
      switch (option) {
        case 'all':
          return {
            from: this.$moment()
                .subtract(30, 'years')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .format('YYYY-MM-DD')
          };
        case 'today':
          return {
            from: this.$moment()
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .format('YYYY-MM-DD')
          };
        case 'yesterday':
          return {
            from: this.$moment()
                .subtract(1, 'days')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .subtract(1, 'days')
                .format('YYYY-MM-DD')
          };
        case 'thisWeek':
          return {
            from: this.$moment()
                .startOf('week')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .endOf('week')
                .format('YYYY-MM-DD')
          };
        case 'lastWeek':
          return {
            from: this.$moment()
                .subtract(1, 'week')
                .startOf('week')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .subtract(1, 'week')
                .endOf('week')
                .format('YYYY-MM-DD')
          };
        case 'last7Days':
          return {
            from: this.$moment()
                .subtract(7, 'days')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .format('YYYY-MM-DD')
          };
        case 'thisMonth':
          return {
            from: this.$moment()
                .startOf('month')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .endOf('month')
                .format('YYYY-MM-DD')
          };
        case 'lastMonth':
          return {
            from: this.$moment()
                .subtract(1, 'month')
                .startOf('month')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .subtract(1, 'month')
                .endOf('month')
                .format('YYYY-MM-DD')
          };
        case 'last30Days':
          return {
            from: this.$moment()
                .subtract(30, 'days')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .format('YYYY-MM-DD')
          };
        case 'thisYear':
          return {
            from: this.$moment()
                .startOf('year')
                .format('YYYY-MM-DD'),
            until: this.$moment()
                .endOf('year')
                .format('YYYY-MM-DD')
          };
        case 'custom':
          return {
            from: this.customRange.start,
            until: this.customRange.end
          };
      }
    },
  }
};
</script>


<style lang="scss" scoped>
.calendar-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(4px);

  &__label {
    display: flex;
    align-items: center;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;

    i {
      color: #007BFF;
    }
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    flex: 1;
    min-width: 0;
  }

  &__pill {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    cursor: pointer;
    outline: none;
    transition: all 0.18s ease;
    white-space: nowrap;

    &:hover {
      background: rgba(0, 123, 255, 0.08);
      color: #007BFF;
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
    }

    &--active {
      color: #ffffff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      border-color: transparent;
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);

      &:hover {
        color: #ffffff;
        background: linear-gradient(135deg, #66b5ff 0%, #1a8cff 60%, #0066cc 100%);
      }
    }

    &--custom {
      margin-left: auto;
      background: #ffffff;
      border-color: rgba(110, 117, 159, 0.22);
      color: #007BFF;
      padding: 0.38rem 0.95rem;

      &:hover {
        border-color: rgba(0, 123, 255, 0.45);
        background: rgba(0, 123, 255, 0.04);
      }

      &.calendar-bar__pill--active {
        background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
        color: #ffffff;
        border-color: transparent;

        .calendar-bar__range-arrow { color: rgba(255,255,255,0.78); }
      }
    }
  }

  &__pill-icon {
    font-size: 0.82rem;
  }

  &__range-arrow {
    margin: 0 0.3rem;
    color: #8a91ad;
    font-weight: 600;
  }
}

// Dark theme overrides (follows Vuexy .dark-layout class on body/html)
::v-deep .dark-layout .calendar-bar,
.dark-layout .calendar-bar {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);
}

.dark-layout .calendar-bar {
  &__label {
    color: #b4b7bd;
    border-right-color: rgba(255, 255, 255, 0.08);

    i { color: #4da3ff; }
  }

  &__pill {
    color: #b4b7bd;

    &:hover {
      background: rgba(77, 163, 255, 0.15);
      color: #b3d7ff;
    }

    &--custom {
      background: #283046;
      border-color: rgba(255, 255, 255, 0.08);
      color: #a8b6ff;

      &:hover {
        background: rgba(77, 163, 255, 0.12);
        border-color: rgba(77, 163, 255, 0.35);
      }
    }
  }

  &__range-arrow {
    color: #8a91ad;
  }
}

@media (max-width: 768px) {
  .calendar-bar {
    gap: 0.4rem;
    padding: 0.5rem 0.6rem;

    &__label {
      display: none;
    }

    &__pill {
      padding: 0.35rem 0.65rem;
      font-size: 0.74rem;
    }

    &__pill--custom {
      margin-left: 0;
    }
  }
}
</style>
